<template>
  <div
    class="fixed inset-0 z-[110] flex items-center justify-center bg-ink/50 sm:px-4 sm:py-6"
  >
    <div ref="dialogPanel" role="dialog" aria-modal="true" tabindex="-1" @keydown="onDialogKeydown"
      :aria-labelledby="titleId"
      class="w-full h-[100dvh] sm:h-auto sm:max-h-[90dvh] sm:max-w-2xl flex flex-col paper-card sm:rounded-md overflow-hidden"
    >
      <div class="flex items-center gap-2 px-4 py-2 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <p :id="titleId" class="min-w-0 flex-1 text-[13px] font-semibold leading-snug line-clamp-2">
          {{ parsed.title }}
        </p>
        <button
          type="button"
          @click="emit('close')"
          class="shrink-0 w-11 h-11 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('liveHome.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div class="overflow-y-auto flex-1 px-4 py-5 sm:px-6 sm:py-6">
        <div class="mb-7">
          <LegalDocumentLanguageSwitch v-model="selectedLocale" />
          <h2 class="mt-4 text-[18px] sm:text-[20px] font-semibold tracking-tight leading-snug">
            {{ parsed.title }}
          </h2>
          <p v-if="parsed.revisionNote" class="mt-2 text-[12px] text-[hsl(var(--muted-foreground))]">
            {{ parsed.revisionNote }}
          </p>
        </div>
        <LegalDocumentBody :markdown="markdown" />
      </div>

      <div class="shrink-0 px-4 py-3 border-t border-[hsl(var(--border)/0.6)]">
        <button
          type="button"
          @click="emit('close')"
          class="w-full h-11 rounded-xl bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
        >
          {{ $t('routeRegistration.terms.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, ref, watch } from "vue";
import { X } from "lucide-vue-next";
import type { LegalDocument } from "../content/legalDocuments";
import { preferredLegalDocumentLocale } from "../content/legalDocuments";
import { parseLegalMarkdown } from "../utils/legalMarkdown";
import LegalDocumentBody from "./LegalDocumentBody.vue";
import LegalDocumentLanguageSwitch from "./LegalDocumentLanguageSwitch.vue";
import { useHistoryBackClose } from "../composables/useHistoryBackClose";

const props = defineProps<{ document: LegalDocument }>();
const emit = defineEmits<{
  (event: "close"): void;
}>();

const selectedLocale = ref(preferredLegalDocumentLocale(props.document));
const markdown = computed(() => props.document.translations[selectedLocale.value] || props.document.translations.kk);
const parsed = computed(() => parseLegalMarkdown(markdown.value));
const titleId = computed(() => `legal-document-${props.document.id}-title`);

watch(() => props.document.id, () => {
  selectedLocale.value = preferredLegalDocumentLocale(props.document);
});

useHistoryBackClose(() => emit("close"));

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => emit('close') });
</script>
